<template>
  <div>
    <b-modal
      ref="modalSyncProductKompack"
      size="lg"
      dialog-class="!max-w-[600px] relative"
      body-class="!p-[32px]"
      static
      hide-footer
      hide-header
      centered
    >
      <b-button
        variant="ghost"
        class="absolute top-[16px] right-[16px] !p-[16px] [&.btn-danger]:!bg-[#E31A1A] !flex items-center justify-center gap-[8px]"
        @click="handleCancel"
      >
        <span class="k-close-circle text-[26px]" />
      </b-button>
      <h3 class="text-[#333333] text-[21px] text-center font-[500] mb-[24px]">
        Hubungkan Produk
      </h3>

      <MappingInfoCard
        marketplace-label="Produk di Gudang Shopee"
        :marketplace="marketplace_product"
        :kompack="kompack_product"
        :is-synced="isSynced"
        :is-disconnecting="isMutatingUnsyncProduct"
        @disconnect="handleProductDisconnection"
      />

      <b-input-group class="mb-[12px]">
        <b-input-group-prepend is-text>
          <span class="k-search-normal-1" />
        </b-input-group-prepend>
        <b-form-input
          v-model="searchedProduct"
          type="search"
          placeholder="Cari produk"
          class="mb-[12px] !border-l-0"
          @input="handleSearchInput"
        />
      </b-input-group>

      <b-table
        :items="preparedProducts"
        :fields="productsFields"
        class="rounded-[8px] border-[1px] border-[#E2E2E2]"
        responsive
        sticky-header
        show-empty
      >
        <template #empty>
          <div class="flex items-center justify-center h-full my-[12px] gap-[8px]">
            <b-spinner
              v-if="productsFetcher && productsFetcher.isLoading"
              small
              variant="primary"
            />
            <span class="text-[#828282] text-[14px] font-[400]">
              {{ tableEmptyText }}
            </span>
          </div>
        </template>

        <template
          #custom-foot
        >
          <td
            v-show="shouldShowLoadMore"
            ref="loadingFooter"
            colspan="100%"
          >
            <div class="flex items-center justify-center h-full my-[12px] gap-[8px]">
              <b-spinner
                small
                variant="primary"
              />
              <span class="text-[#828282] text-[14px] font-[400]">
                memuat produk lainnya...
              </span>
            </div>
          </td>
        </template>

        <template #cell(action)="data">
          <BtnConnectProduct
            :ref="`btnConnectProduct-${data.index}`"
            :disabled="isMutatingSyncProduct"
            :is-connected="data.item.is_connected"
            :is-synced="isSynced"
            @click="handleSyncProduct(data)"
          >
            {{ data.item.is_connected ? 'Terhubung' : 'Hubungkan' }}
          </BtnConnectProduct>
        </template>
      </b-table>
    </b-modal>

    <ModalConfirm
      ref="modalConfirm"
    />
  </div>
</template>

<script>
import { productsFields } from './config'
import useProductList from './composables/useProductList'
import useDebouncedSearch from './composables/useDebouncedSearch'
import useIntersectionObserver from './composables/useIntersectionObserver'
import BtnConnectProduct from './BtnConnectProduct.vue'
import ModalConfirm from '../ModalConfirm.vue'
import MappingInfoCard from '../MappingInfoCard.vue'

export default {
  name: 'ModalSyncProduct',

  components: {
    BtnConnectProduct,
    ModalConfirm,
    MappingInfoCard,
  },

  data() {
    return {
      productsFields,
      shopId: '',
      index: null,
      isSynced: false,
      marketplace_product: {
        name: '',
        sku: '',
        id: '',
      },
      kompack_product: {
        name: '',
        sku: '',
      },
      isMutatingSyncProduct: false,
      is_variant: 0,
      searchedProduct: '',
      lastSearchedKeyword: '',
      searchDebounceId: null,
      productsFetcher: null,
      isMutatingUnsyncProduct: false,
    }
  },

  computed: {
    preparedProducts() {
      if (!this.productsFetcher?.data) {
        return []
      }
      return this.prepareProducts(
        this.productsFetcher.data,
        this.kompack_product.id,
      )
    },
    tableEmptyText() {
      if (!this.productsFetcher || this.productsFetcher.isLoading) {
        return 'Memuat data...'
      }
      if (this.searchedProduct) {
        return `Tidak ada data yang ditemukan untuk "${this.searchedProduct}"`
      }
      return 'Tidak ada data yang ditemukan'
    },
    shouldShowLoadMore() {
      return (
        this.productsFetcher
        && this.productsFetcher.hasMore
      )
    },
    realSKU() {
      return this.payload?.product_sku || ''
    },
  },
  created() {
    this.initializeComponent()
    this.initializeComposables()
  },
  mounted() {
    this.setupIntersectionObserver()
  },
  beforeDestroy() {
    this.cleanup()
  },
  methods: {
    // Initialize state and fetcher
    initializeComponent() {
      const { createProductsFetcher, prepareProducts } = useProductList({
        http: this.$http_new,
        onError: message => this.$toast_error({ message }),
      })

      this.prepareProducts = prepareProducts
      this.productsFetcher = createProductsFetcher()
    },
    // Initialize search and intersection observer
    initializeComposables() {
      const { handleSearchInput, cleanup: cleanupSearch } = useDebouncedSearch(
        this,
        keyword => {
          this.performSearch(keyword)
        },
        500,
      )
      this.handleSearchInput = handleSearchInput
      this.cleanupSearch = cleanupSearch

      const {
        setup: setupObserver,
        observe: observeElement,
        cleanup: cleanupObserver,
      } = useIntersectionObserver({
        onIntersect: entry => {
          if (
            entry.isIntersecting
            && this.productsFetcher
            && this.productsFetcher.hasMore
            && !this.productsFetcher.isLoading
          ) {
            this.productsFetcher.fetchData()
          }
        },
      })
      this.setupObserver = setupObserver
      this.observeElement = observeElement
      this.cleanupObserver = cleanupObserver
    },
    // Cleanup search and intersection observer
    cleanup() {
      if (this.cleanupSearch) {
        this.cleanupSearch()
      }
      if (this.cleanupObserver) {
        this.cleanupObserver()
      }
    },
    // Reset state
    resetState() {
      this.isSynced = false
      this.marketplace_product = {
        name: '',
        sku: '',
        id: '',
      }
      this.kompack_product = {
        name: '',
        sku: '',
      }
      this.isMutatingSyncProduct = false
      this.searchedProduct = ''
      this.isMutatingUnsyncProduct = false
      this.shopId = ''
      this.index = null
      this.cleanup()
    },
    // Modal Control: Show & Setup Intersection Observer
    show() {
      this.$refs.modalSyncProductKompack.show()
      this.productsFetcher.fetchData({
        reset: true,
        options: {
          keyword: this.searchedProduct,
          is_variant: this.is_variant,
        },
      })
      this.$nextTick(() => {
        this.observeLoadingFooter()
      })
    },
    // Modal Control: Hide & Reset State
    closeModal() {
      this.$refs.modalSyncProductKompack.hide()
      this.resetState()
    },
    handleCancel() {
      this.closeModal()
      this.$emit('cancel')
    },
    // Data Management: Set Data
    setData(data) {
      this.is_variant = data.total_variant > 0 ? 1 : 0
      this.isSynced = data.is_synced
      this.shopId = data.shop_id
      this.index = data.index
      this.marketplace_product = {
        name: data.marketplace_product_name,
        sku: data.marketplace_sku,
        id: data.marketplace_product_id,
      }
      this.kompack_product = {
        name: data.product_name,
        sku: data.sku,
        id: data.product_id,
      }
      this.handleProductFetch(data.shop_id)
    },
    // Data Management: Handle Product Fetch
    handleProductFetch(shopId) {
      const shouldFetch = this.shopId !== shopId
        || (this.shopId === shopId && this.productsFetcher.data.length === 0)

      if (shouldFetch) {
        this.productsFetcher.fetchData({
          reset: true,
          options: {
            keyword: this.searchedProduct,
            is_variant: this.is_variant,
          },
        })
      }
    },
    // Data Management: Perform Search
    performSearch(keyword) {
      const fetchPromise = this.productsFetcher.fetchData({
        reset: true,
        options: {
          keyword,
          is_variant: this.is_variant,
        },
      })

      // Re-observe loading footer after fetch completes (jika ada)
      if (fetchPromise?.then) {
        fetchPromise.then(() => {
          this.$nextTick(() => {
            this.observeLoadingFooter()
          })
        })
      } else {
        this.$nextTick(() => {
          this.observeLoadingFooter()
        })
      }
    },
    // Setup intersection observer
    setupIntersectionObserver() {
      this.$nextTick(() => {
        const { loadingFooter } = this.$refs
        if (!loadingFooter || !this.productsFetcher) {
          return
        }

        if (this.setupObserver) {
          this.setupObserver(loadingFooter)
        }
      })
    },
    // Re-observe loading footer
    observeLoadingFooter() {
      this.$nextTick(() => {
        const { loadingFooter } = this.$refs
        if (!loadingFooter || !this.productsFetcher) {
          return
        }

        if (this.observeElement) {
          this.observeElement(loadingFooter)
        } else if (this.setupObserver) {
          this.setupObserver(loadingFooter)
        }
      })
    },
    async handleSyncProduct(data) {
      this.$refs[`btnConnectProduct-${data.index}`].setLoading(true)
      this.isMutatingSyncProduct = true
      try {
        const payload = [{
          product_id: data.item.id,
          marketplace_product_id: this.marketplace_product.id,
        }]

        await this.$http_new.post(
          `/komship/api/v1/marketplace-products/shops/${this.shopId}/sync`,
          payload,
        )

        this.$emit('success', {
          type: 'sync',
          index: this.index,
          product_name: data.item.name,
          product_sku: data.item.sku,
          product_id: data.item.id,
        })
      } catch (error) {
        const isAlreadySynced = error.response?.data?.meta?.message.includes('already synced with another marketplace product')
        this.$toast_error({
          message: isAlreadySynced ? 'Produk ini sudah terhubung dengan produk marketplace lain' : 'Gagal menghubungkan produk dengan marketplace, silahkan coba lagi',
        })
      } finally {
        this.isMutatingSyncProduct = false
        this.$refs[`btnConnectProduct-${data.index}`].setLoading(false)
      }
    },
    async handleProductDisconnection() {
      this.isMutatingUnsyncProduct = true

      this.$refs.modalConfirm.show({
        title: 'Apakah Kamu Yakin Akan Memutuskan Produk?',
        status: 'WARNING',
        description: 'Jika kamu memutuskan produk ini, produk tidak akan terhubung lagi dan Kamu tidak bisa melakukan transaksi pada produk ini.',
        confirmLabel: 'Ya',
        cancelLabel: 'Batal',
        onConfirm: async () => {
          const payload = {
            product_id: this.kompack_product.id,
            marketplace_product_id: this.marketplace_product.id,
          }

          try {
            await this.$http_new.post(
              `/komship/api/v1/marketplace-products/shops/${this.shopId}/unsync`,
              payload,
            )

            this.$refs.modalConfirm.show({
              status: 'SUCCESS',
              title: 'Produk Kamu Telah Terputus',
              description: 'Produk marketplace dan gudang kompack kamu sekarang sudah terputus. Kamu tidak bisa melakukan transaksi pada produk ini.',
              confirmLabel: 'Selesai',
              onConfirm: () => {
                this.$emit('success', {
                  type: 'unsync',
                  index: this.index,
                })

                this.$refs.modalConfirm.hide()
              },
            })
          } catch (error) {
            this.$toast_error({
              message: 'Gagal memutuskan produk dengan marketplace, silahkan coba lagi',
            })
          } finally {
            this.isMutatingUnsyncProduct = false
          }
        },
        onCancel: () => {
          this.$refs.modalConfirm.hide()
          this.isMutatingUnsyncProduct = false
        },
      })
    },
  },
}
</script>
